import React from 'react';
import moment from 'moment';
import { I18n } from '@aws-amplify/core';
import type { MenuProps } from 'antd';
import type { ActionPlan } from '@/types/Bera';
import * as S from './styles';
import { MoreActions } from '@/components/views/BeraJob/Results/ActionPlans/ActionPlanCard/MoreActions';

interface ActionPlanCardProps {
	data: ActionPlan;
	onMoreActions?: (id: string) => void;
	menuItems?: MenuProps['items'];
}

const STATUS_COLORS: Record<string, string> = {
	todo: '#bfbfbf',
	doing: '#ffb40a',
	done: '#52c41a'
};

const STATUS_LABELS: Record<string, string> = {
	todo: 'To do',
	doing: 'Doing',
	done: 'Done'
};

function formatDate(date: string): string {
	if (!date) return '-';
	return moment(date).format('DD/MM/YYYY');
}

function calculateDaysRemaining(dueDate: string): number {
	if (!dueDate) return 0;
	const today = moment().startOf('day');
	const due = moment(dueDate).startOf('day');
	return due.diff(today, 'days');
}

function getStatus(dueDate: string): 'todo' | 'doing' | 'done' {
	const days = calculateDaysRemaining(dueDate);
	if (days < 0) return 'done';
	if (days <= 7) return 'doing';
	return 'todo';
}

export function ActionPlanCard({ data, onMoreActions, menuItems }: Readonly<ActionPlanCardProps>): JSX.Element {
	const daysRemaining = calculateDaysRemaining(data.due_date);
	const status = getStatus(data.due_date);

	function handleOnClick(): void {
		onMoreActions?.(data.id);
	}

	return (
		<S.Container>
			<S.Header>
				<S.HeaderTitle>{data.title}</S.HeaderTitle>
			</S.Header>

			<S.InfoSection>
				<S.InfoRow>
					<S.InfoItem>
						<S.Label>{I18n.get('Responsible')}:</S.Label> <S.Value>{data.responsible?.name || '-'}</S.Value>
					</S.InfoItem>
					<S.InfoItem className="center">
						<S.Label>{I18n.get('Deadline')}:</S.Label>{' '}
						<S.Value>
							{formatDate(data.due_date)} ({daysRemaining} {I18n.get('days')})
						</S.Value>
					</S.InfoItem>
					<S.InfoItem className="right">
						<S.Label>{I18n.get('Status')}:</S.Label>
						<S.StatusContainer>
							<S.StatusDot $color={STATUS_COLORS[status]} />
							<S.StatusText>{I18n.get(STATUS_LABELS[status])}</S.StatusText>
						</S.StatusContainer>
					</S.InfoItem>
				</S.InfoRow>

				<S.DescriptionRow>
					<S.DescriptionText>
						<span className="label">{I18n.get('Description')}:</span>{' '}
						<span className="value">{data.description || '-'}</span>
					</S.DescriptionText>
				</S.DescriptionRow>
			</S.InfoSection>
			<S.Divider />
			<S.TasksHeader>
				<S.TasksHeaderTitle>{I18n.get('Tasks')}</S.TasksHeaderTitle>
			</S.TasksHeader>
			{data.tasks && data.tasks.length > 0 ? (
				data.tasks.map((task) => (
					<S.TaskRow key={task.id}>
						<S.TaskCell>{task.title}</S.TaskCell>
						<S.TaskDivider />
						<S.TaskCell>{task.responsible?.name || '-'}</S.TaskCell>
						<S.TaskDivider />
						<S.TaskCell>{formatDate(task.due_date)}</S.TaskCell>
					</S.TaskRow>
				))
			) : (
				<S.EmptyState>{I18n.get('No tasks')}</S.EmptyState>
			)}
			<MoreActions menuItems={menuItems} onClick={handleOnClick} />
		</S.Container>
	);
}
